import api from '~/utils/api';
import { pendingClaimLineItemToCalculatedLineItem } from '~/utils/claimLineItem';
import { ClaimType, ReturnShipmentType } from '~/utils/constants';
import { makeAction, makeLoader, redirect } from '~/utils/routing';
import { AsyncReturnType } from '~/utils/types';

import { StatusCodes } from 'http-status-codes';
import { zfd } from 'zod-form-data';
import { crewClaimContext } from '~/app/features/crew/crewClaimMiddleware';
import { crewOrderContext } from '~/app/features/order-middleware/orderMiddleware';
import { settingsContext } from '~/app/settingsMiddleware';
import { shipmentMethodSchema } from '~/utils/schemas';

type ShipmentQuote = AsyncReturnType<typeof api.createShipmentMethodsQuote>;
type ShipmentMethod = ShipmentQuote['fromCustomer'][number];

const textNameMap = {
  [ReturnShipmentType.packingSlip]: 'Pack & Ship',
  [ReturnShipmentType.label]: 'Use Provided Label',
  [ReturnShipmentType.consolidatedDropoff]: '',
} satisfies Record<ReturnShipmentType, string>;

const detailText = {
  [ReturnShipmentType.packingSlip]: 'Box & Label Needed',
  [ReturnShipmentType.label]: 'Box Needed',
  [ReturnShipmentType.consolidatedDropoff]: '',
} satisfies Record<ReturnShipmentType, string>;

// Happy Returns leads, then the carrier label, then pack & ship
const typeSortWeight = {
  [ReturnShipmentType.consolidatedDropoff]: 1,
  [ReturnShipmentType.label]: 2,
  [ReturnShipmentType.packingSlip]: 3,
} satisfies Record<ReturnShipmentType, number>;

export class NoReturnMethodsError extends Error {
  constructor() {
    super('No return methods available');
    this.name = 'NoReturnMethodsError';
  }
}

const getLabel = (method: ShipmentMethod) => {
  if (method.type === ReturnShipmentType.packingSlip) {
    return textNameMap[method.type];
  }

  if (method.type === ReturnShipmentType.label) {
    return `${textNameMap[method.type]} - (${method.shipmentFromPlatform.rate.carrier})`;
  }

  return '';
};

export const { loader, useLoaderData } = makeLoader(({ context }, appStore) => {
  const { id, globalEOrderId } = context.get(crewOrderContext);
  const { storefrontId } = context.get(settingsContext);
  const { claimLineItems, customerInfo, claimType } =
    context.get(crewClaimContext);

  if (customerInfo === null) {
    throw new Error('Missing shipping address');
  }

  return api
    .createShipmentMethodsQuote({
      params: {
        storefrontId,
      },
      body: {
        id,
        globalEOrderId,
        claimType,
        address: {
          firstName: customerInfo.firstName,
          lastName: customerInfo.lastName,
          ...customerInfo.address,
        },
        // the email the customer just confirmed, which becomes the claim's customerEmail
        email: customerInfo.email,
        calculatedLineItems: claimLineItems.map((item) => ({
          ...pendingClaimLineItemToCalculatedLineItem(item),
          grams: item.grams * item.quantity,
        })),
      },
    })
    .then(({ toCustomer, fromCustomer }) => {
      if (fromCustomer.length === 0) {
        throw new NoReturnMethodsError();
      }

      if (toCustomer) {
        appStore.dispatch(
          appStore.actions.crewClaim.setToCustomerRate(toCustomer),
        );
      }

      if (fromCustomer.length === 1 && fromCustomer[0]) {
        appStore.dispatch(
          appStore.actions.crewClaim.setReturnMethod(fromCustomer[0]),
        );

        const reviewPage =
          claimType === ClaimType.warranty
            ? `../warranty-review`
            : `../return-summary`;

        return redirect(reviewPage, StatusCodes.SEE_OTHER);
      }

      return {
        methods: fromCustomer
          .sort((a, b) => typeSortWeight[a.type] - typeSortWeight[b.type])
          .map((method) => ({
            ...method,
            label: getLabel(method),
            description: detailText[method.type],
          })),
      };
    });
});

export const FIELD_KEY = 'return-method';

const formDataSchema = zfd.formData({
  [FIELD_KEY]: zfd.json(shipmentMethodSchema),
});

export const { action, useActionResult } = makeAction(
  ({ formData, context }, appStore) => {
    const { claimType } = context.get(crewClaimContext);
    const result = formDataSchema.safeParse(formData);

    if (!result.success) {
      return {
        ok: false,
        message: 'Invalid value for return method',
      };
    }

    const method = result.data[FIELD_KEY];

    appStore.dispatch(appStore.actions.crewClaim.setReturnMethod(method));

    const reviewPage =
      claimType === ClaimType.warranty
        ? `../warranty-review`
        : `../return-summary`;

    return redirect(reviewPage);
  },
);
